import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import { toast } from "react-hot-toast";
import useAccountantService from "../../api/services/AccountantService";
import useTermService from "../../api/services/termService";
import * as yup from "yup";
import { getFirstTermDebtLabel } from "../../utils/utils";

// Helper functions for number formatting (allow optional leading minus for debts)
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return "";
  const str = value.toString().trim();
  if (str === "" || str === "-") return str;
  const isNegative = str.includes("-");
  const numericPart = str.replace(/-/g, "").replace(/[^\d.]/g, "");
  if (numericPart === "") return isNegative ? "-" : "";
  const formatted = numericPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return isNegative ? "-" + formatted : formatted;
};

const parseNumberFromFormatted = (formattedValue) => {
  if (formattedValue === null || formattedValue === undefined || formattedValue === "") return "";
  const str = formattedValue.toString().trim();
  if (str === "-") return "-0";
  const isNegative = str.includes("-");
  const withoutCommas = str.replace(/-/g, "").replace(/,/g, "");
  const num = parseFloat(withoutCommas);
  if (isNaN(num)) return "";
  return isNegative ? String(-num) : String(num);
};

/** Allowed input for debt fields: optional minus, digits, optional decimal. Keeps minus sign while typing. */
const DEBT_FIELD_PATTERN = /^-?\d*\.?\d*$/;
const isDebtField = (field) => field === "firstTermDebt" || field === "secondTermDebt";

/** Same formula as ClassStudentsAccountsData: totalFees - totalPaid */
const getCalculatedDebtBalance = (values) => {
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const parsed = parseNumberFromFormatted(String(v));
    if (parsed === "" || parsed === "-0") return 0;
    const n = parseFloat(parsed);
    return isNaN(n) ? 0 : n;
  };
  const paid1 = toNum(values.firstTermPaidAmount);
  const paid2 = toNum(values.secondTermPaidAmount);
  const totalPaid = paid1 + paid2;
  const totalFees =
    toNum(values.firstTermDebt) + toNum(values.secondTermDebt) +
    toNum(values.firstTermFee) + toNum(values.secondTermFee) +
    toNum(values.firstTermExamFee) + toNum(values.secondTermExamFee) +
    toNum(values.firstTermOtherContribution) + toNum(values.secondTermOtherContribution) +
    toNum(values.firstTermParentFee) + toNum(values.secondTermParentFee);
  return totalFees - totalPaid;
};

const formatBalanceDisplay = (num) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);


const CreateAccountDetailsSchema = yup.object().shape({
  termId: yup.string().required("Term is required"),
  firstTermDebt: yup.string().test('is-valid-amount', 'Please enter a valid amount (negative allowed)', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return numericValue !== "" && !isNaN(parseFloat(numericValue));
  }),
  secondTermDebt: yup.string().test('is-valid-amount', 'Please enter a valid amount (negative allowed)', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return numericValue !== "" && !isNaN(parseFloat(numericValue));
  }),
  firstTermFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermExamFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermExamFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermOtherContribution: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermOtherContribution: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermParentFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermParentFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermPaidAmount: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermPaidAmount: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
});

const ELIGIBLE_TERM_STATUSES = ["ACTIVE", "ACCOUNTS_STATUS"];

const getEligibleTerms = (terms) =>
  Array.isArray(terms)
    ? terms.filter((t) => ELIGIBLE_TERM_STATUSES.includes(t.status))
    : [];

const getInitialValues = (studentId, existingData = null, terms = []) => {
  const eligible = getEligibleTerms(terms);
  const defaultTermId =
    existingData?.termId ||
    (eligible.length > 0
      ? (eligible.find((t) => t.status === "ACTIVE")?.id || eligible[0]?.id || "")
      : "");
  return {
  studentId: studentId || "",
  termId: defaultTermId,
  firstTermDebt: existingData?.firstTermDebt !== null && existingData?.firstTermDebt !== undefined ? String(Number(existingData.firstTermDebt)) : "",
  secondTermDebt: existingData?.secondTermDebt !== null && existingData?.secondTermDebt !== undefined ? String(Number(existingData.secondTermDebt)) : "",
  firstTermFee: existingData?.firstTermFee ? formatNumberWithCommas(existingData.firstTermFee) : "",
  secondTermFee: existingData?.secondTermFee ? formatNumberWithCommas(existingData.secondTermFee) : "",
  firstTermExamFee: existingData?.firstTermExamFee ? formatNumberWithCommas(existingData.firstTermExamFee) : "",
  secondTermExamFee: existingData?.secondTermExamFee ? formatNumberWithCommas(existingData.secondTermExamFee) : "",
  firstTermOtherContribution: existingData?.firstTermOtherContribution ? formatNumberWithCommas(existingData.firstTermOtherContribution) : "",
  secondTermOtherContribution: existingData?.secondTermOtherContribution ? formatNumberWithCommas(existingData.secondTermOtherContribution) : "",
  firstTermParentFee: existingData?.firstTermParentFee ? formatNumberWithCommas(existingData.firstTermParentFee) : "",
  secondTermParentFee: existingData?.secondTermParentFee ? formatNumberWithCommas(existingData.secondTermParentFee) : "",
  firstTermPaidAmount: existingData?.firstTermPaidAmount ? formatNumberWithCommas(existingData.firstTermPaidAmount) : "",
  secondTermPaidAmount: existingData?.secondTermPaidAmount ? formatNumberWithCommas(existingData.secondTermPaidAmount) : "",
  };
};

const CreateNewAccountDetails = ({ open, onClose, onSuccess, studentId, studentName, classId, classTitle, editingRow }) => {
  const { createAccountantDataByTermStudent, updatePartialAccountantDataByTermStudent } = useAccountantService();
  const { fetchAllTermsForAccountsDetails, terms } = useTermService();
  const [existingData, setExistingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const eligibleTerms = React.useMemo(() => getEligibleTerms(terms), [terms]);

  
  useEffect(() => {
    if (open) {
      fetchAllTermsForAccountsDetails();
    }
  }, [open]);

  
  // Pre-fill only when editing a specific row. "Add another term" / new record → empty form (no API pre-load).
  useEffect(() => {
    const loadExistingData = async () => {
      if (!open || !studentId) {
        if (!open) setExistingData(null);
        return;
      }

      if (editingRow?.originalData) {
        setIsLoading(true);
        try {
          setExistingData(editingRow.originalData);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      setExistingData(null);
      setIsLoading(false);
    };

    loadExistingData();
  }, [open, studentId, editingRow]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const dto = {
        studentId: studentId,
        termId: values.termId,
        classId: classId || existingData?.classId || null,
      };
      
      const firstDebt = values.firstTermDebt !== "" && values.firstTermDebt != null ? parseNumberFromFormatted(values.firstTermDebt) : "";
      const secondDebt = values.secondTermDebt !== "" && values.secondTermDebt != null ? parseNumberFromFormatted(values.secondTermDebt) : "";
      if (firstDebt !== "" && firstDebt !== "-0") dto.firstTermDebt = parseFloat(firstDebt);
      if (secondDebt !== "" && secondDebt !== "-0") dto.secondTermDebt = parseFloat(secondDebt);
      if (values.firstTermFee) dto.firstTermFee = parseNumberFromFormatted(values.firstTermFee);
      if (values.secondTermFee) dto.secondTermFee = parseNumberFromFormatted(values.secondTermFee);
      if (values.firstTermExamFee) dto.firstTermExamFee = parseNumberFromFormatted(values.firstTermExamFee);
      if (values.secondTermExamFee) dto.secondTermExamFee = parseNumberFromFormatted(values.secondTermExamFee);
      if (values.firstTermOtherContribution) dto.firstTermOtherContribution = parseNumberFromFormatted(values.firstTermOtherContribution);
      if (values.secondTermOtherContribution) dto.secondTermOtherContribution = parseNumberFromFormatted(values.secondTermOtherContribution);
      if (values.firstTermParentFee) dto.firstTermParentFee = parseNumberFromFormatted(values.firstTermParentFee);
      if (values.secondTermParentFee) dto.secondTermParentFee = parseNumberFromFormatted(values.secondTermParentFee);
      if (values.firstTermPaidAmount) dto.firstTermPaidAmount = parseNumberFromFormatted(values.firstTermPaidAmount);
      if (values.secondTermPaidAmount) dto.secondTermPaidAmount = parseNumberFromFormatted(values.secondTermPaidAmount);

      // Try to update first (if record exists), otherwise create
      if (existingData && existingData.id) {
        try {
          
          await updatePartialAccountantDataByTermStudent(values.termId, studentId, dto, () => {
            onSuccess?.();
            onClose();
          });
        } catch (updateError) {
          // If update fails with 404, record was deleted, so create it
          if (updateError.response?.status === 404) {
            await createAccountantDataByTermStudent(dto, () => {
              onSuccess?.();
              onClose();
            });
          } else {
            throw updateError;
          }
        }
      } else {
        // Create new record
        await createAccountantDataByTermStudent(dto, () => {
          onSuccess?.();
          onClose();
        });
      }
    } catch (error) {
      console.error("Error saving account details:", error);
      toast.error(error.response?.data?.message || "Failed to save account details");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        CREATE NEW ACCOUNT DETAILS
        {studentId && studentName && (
          <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '8px', color: '#666' }}>
            Managing account details for: <strong>{studentName}</strong>
          </div>
        )}
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={getInitialValues(studentId, existingData, eligibleTerms)}
          validationSchema={CreateAccountDetailsSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Grid container spacing={2} mt={1}>
                
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    select
                    name="termId"
                    label="Term"
                    fullWidth
                    variant="filled"
                    value={values.termId || (eligibleTerms.length > 0 ? (eligibleTerms.find((t) => t.status === "ACTIVE")?.id || eligibleTerms[0]?.id || "") : "")}
                    error={touched.termId && !!errors.termId}
                    helperText={touched.termId && errors.termId}
                    onChange={(e) => {
                      setFieldValue("termId", e.target.value);
                    }}
                    disabled={eligibleTerms.length === 0}
                  >
                    {eligibleTerms.length > 0 ? (
                      eligibleTerms.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.termName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        No terms available
                      </MenuItem>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Debt Balance"
                    variant="filled"
                    value={formatBalanceDisplay(getCalculatedDebtBalance(values))}
                    InputProps={{ readOnly: true }}
                    helperText="Calculated automatically (Total fees − Total paid)"
                  />
                </Grid>

                {[
                  { field: "firstTermDebt", label: getFirstTermDebtLabel(classTitle) },
                  { field: "secondTermDebt", label: "Second Term Debt" },
                  { field: "firstTermFee", label: "First Term Fee" },
                  { field: "secondTermFee", label: "Second Term Fee" },
                  { field: "firstTermExamFee", label: "First Term Exam Fee" },
                  { field: "secondTermExamFee", label: "Second Term Exam Fee" },
                  { field: "firstTermOtherContribution", label: "First Term Other Contribution" },
                  { field: "secondTermOtherContribution", label: "Second Term Other Contribution" },
                  { field: "firstTermParentFee", label: "First Term Parent Fee" },
                  { field: "secondTermParentFee", label: "Second Term Parent Fee" },
                  { field: "firstTermPaidAmount", label: "First Term Paid Amount" },
                  { field: "secondTermPaidAmount", label: "Second Term Paid Amount" },
                ].map(({ field, label }) => {
                  const isDebt = isDebtField(field);
                  return (
                    <Grid item xs={12} lg={6} key={field}>
                      <TextField
                        fullWidth
                        label={label}
                        variant="filled"
                        value={isDebt ? (values[field] ?? "") : formatNumberWithCommas(values[field])}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (isDebt) {
                            if (DEBT_FIELD_PATTERN.test(input)) setFieldValue(field, input);
                          } else {
                            const formattedValue = formatNumberWithCommas(input);
                            setFieldValue(field, formattedValue);
                          }
                        }}
                        error={touched[field] && !!errors[field]}
                        helperText={touched[field] && errors[field]}
                        placeholder={isDebt ? "e.g. -300000" : "0"}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CreateNewAccountDetails;

